/**
 * Unified Status Management Utility
 * 
 * A configuration-driven approach to handle all status types in the system.
 * Provides consistent methods for all status types without requiring separate classes.
 * 
 * @module statusUtils
 */

import { IconType } from 'react-icons'
import { 
  FiCheckCircle, 
  FiXCircle, 
  FiAlertTriangle, 
  FiClock, 
  FiPauseCircle,
  FiCircle,
  FiDollarSign,
  FiRefreshCw,
  FiShield,
  FiUser,
  FiEye,
  FiSettings,
  FiUserCheck,
  FiPackage,
  FiTruck,
  FiLock,
  FiUnlock
} from 'react-icons/fi'

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Configuration for a single status value
 */
export interface StatusConfig {
  id: string                    // Full ID with prefix (e.g., 'mst_active')
  displayText: string           // Human-readable text
  description?: string          // Detailed description
  colorScheme: string          // Chakra UI color scheme
  icon?: IconType              // Optional React Icon
  sortOrder?: number           // Display order
  isActive?: boolean           // Whether status is currently active/usable
}

/**
 * Configuration for a status type
 */
export interface StatusTypeConfig {
  prefix?: string               // ID prefix (e.g., 'mst_', 'ost_')
  defaultStatus?: string        // Default status if invalid ID provided
  statuses: StatusConfig[]      // Array of status configurations
}

/**
 * All supported status types in the system
 */
export type StatusType = 
  | 'merchant_status'
  | 'order_status'
  | 'address_verification_status'
  | 'order_receivers_registry_status'
  | 'contract_deployments_status'
  | 'settlement_records_status'
  | 'refund_operations_status'
  | 'contract_initialization_status'
  | 'subscription_status'
  | 'admin_role'
  | 'wallet_connection_status'
  | 'api_key_status'
  | 'merchant_wallet_status'

// ============================================================================
// Status Configurations
// ============================================================================

/**
 * Central configuration for all status types in the system
 */
const STATUS_CONFIGURATIONS: Record<StatusType, StatusTypeConfig> = {
  // Merchant Status
  merchant_status: {
    prefix: 'mst_',
    defaultStatus: 'mst_pending_config',
    statuses: [
      {
        id: 'mst_pending_config',
        displayText: 'Pending Configuration',
        description: 'New merchant needs basic configuration',
        colorScheme: 'orange',
        icon: FiSettings,
        sortOrder: 1
      },
      {
        id: 'mst_partial_active',
        displayText: 'Partial Active',
        description: 'Some enabled chains are available, but not all enabled chains are ready',
        colorScheme: 'blue',
        icon: FiAlertTriangle,
        sortOrder: 2
      },
      {
        id: 'mst_active',
        displayText: 'Active',
        description: 'Activated, all features available',
        colorScheme: 'green',
        icon: FiCheckCircle,
        sortOrder: 3
      },
      {
        id: 'mst_suspended',
        displayText: 'Suspended',
        description: 'Temporarily disabled, can be restored',
        colorScheme: 'red',
        icon: FiPauseCircle,
        sortOrder: 4
      },
      {
        id: 'mst_terminated',
        displayText: 'Terminated',
        description: 'Permanently disabled, cannot be restored',
        colorScheme: 'red',
        icon: FiXCircle,
        sortOrder: 5
      }
    ]
  },

  // Order Status
  order_status: {
    prefix: 'ost_',
    defaultStatus: 'ost_awaiting_payment',
    statuses: [
      {
        id: 'ost_awaiting_payment',
        displayText: 'Awaiting Payment',
        description: 'Contract deployed, waiting for payment',
        colorScheme: 'yellow',
        icon: FiClock,
        sortOrder: 1
      },
      {
        id: 'ost_underpaid',
        displayText: 'Underpaid',
        description: 'Received amount is less than required',
        colorScheme: 'orange',
        icon: FiAlertTriangle,
        sortOrder: 2
      },
      {
        id: 'ost_fullpaid',
        displayText: 'Fully Paid',
        description: 'Received exact required amount',
        colorScheme: 'green',
        icon: FiCheckCircle,
        sortOrder: 3
      },
      {
        id: 'ost_overpaid',
        displayText: 'Overpaid',
        description: 'Received amount exceeds required amount',
        colorScheme: 'blue',
        icon: FiDollarSign,
        sortOrder: 4
      },
      {
        id: 'ost_expired',
        displayText: 'Expired',
        description: 'Order expired without payment',
        colorScheme: 'gray',
        icon: FiClock,
        sortOrder: 5
      },
      {
        id: 'ost_settled',
        displayText: 'Settled',
        description: 'Funds distributed to merchant and platform',
        colorScheme: 'green',
        icon: FiCheckCircle,
        sortOrder: 6
      },
      {
        id: 'ost_full_refunded',
        displayText: 'Full Refunded',
        description: 'Payment returned to buyer',
        colorScheme: 'purple',
        icon: FiRefreshCw,
        sortOrder: 7
      },
      {
        id: 'ost_compliance_hold',
        displayText: 'Compliance Hold',
        description: 'Operations suspended due to compliance requirements',
        colorScheme: 'red',
        icon: FiShield,
        sortOrder: 8
      },
      {
        id: 'ost_closed',
        displayText: 'Closed',
        description: 'Order closed by admin; terminal state',
        colorScheme: 'gray',
        icon: FiLock,
        sortOrder: 9
      }
    ]
  },

  // Address Verification Status
  address_verification_status: {
    prefix: 'avs_',
    defaultStatus: 'avs_pending',
    statuses: [
      {
        id: 'avs_pending',
        displayText: 'Pending',
        description: 'Deployment completed, waiting for address comparison verification',
        colorScheme: 'yellow',
        icon: FiClock,
        sortOrder: 1
      },
      {
        id: 'avs_verified',
        displayText: 'Verified',
        description: 'Actual address matches predicted address completely',
        colorScheme: 'green',
        icon: FiCheckCircle,
        sortOrder: 2
      },
      {
        id: 'avs_failed',
        displayText: 'Failed',
        description: 'Actual address does not match predicted address',
        colorScheme: 'red',
        icon: FiXCircle,
        sortOrder: 3
      }
    ]
  },

  // Order Receivers Registry Status
  order_receivers_registry_status: {
    prefix: 'orrs_',
    defaultStatus: 'orrs_active',
    statuses: [
      {
        id: 'orrs_active',
        displayText: 'Active',
        description: 'Pending Deployment - Ready to deploy contract, waiting for deployment trigger',
        colorScheme: 'yellow',
        icon: FiPackage,
        sortOrder: 1
      },
      {
        id: 'orrs_on_hold',
        displayText: 'On Hold',
        description: 'Deployment On Hold - Temporarily suspended deployment operation',
        colorScheme: 'orange',
        icon: FiPauseCircle,
        sortOrder: 2
      },
      {
        id: 'orrs_deployed',
        displayText: 'Deployed',
        description: 'Deployed - Contract successfully deployed to blockchain',
        colorScheme: 'green',
        icon: FiCheckCircle,
        sortOrder: 3
      },
      {
        id: 'orrs_cancelled',
        displayText: 'Cancelled',
        description: 'Deployment Cancelled - Deployment operation has been cancelled',
        colorScheme: 'gray',
        icon: FiXCircle,
        sortOrder: 4
      }
    ]
  },

  // Contract Deployments Status
  contract_deployments_status: {
    prefix: 'cds_',
    defaultStatus: 'cds_failed',
    statuses: [
      {
        id: 'cds_successful',
        displayText: 'Successful',
        description: 'Contract deployment successful, address verification passed',
        colorScheme: 'green',
        icon: FiCheckCircle,
        sortOrder: 1
      },
      {
        id: 'cds_failed',
        displayText: 'Failed',
        description: 'Deployment transaction failed or rejected',
        colorScheme: 'red',
        icon: FiXCircle,
        sortOrder: 2
      },
      {
        id: 'cds_reverted',
        displayText: 'Reverted',
        description: 'Deployment transaction executed but contract creation failed',
        colorScheme: 'orange',
        icon: FiAlertTriangle,
        sortOrder: 3
      }
    ]
  },

  // Settlement Records Status
  settlement_records_status: {
    prefix: 'srs_',
    defaultStatus: 'srs_pending',
    statuses: [
      {
        id: 'srs_pending',
        displayText: 'Pending',
        description: 'Settlement queued and awaiting execution',
        colorScheme: 'yellow',
        icon: FiTruck,
        sortOrder: 1
      },
      {
        id: 'srs_settled',
        displayText: 'Settled',
        description: 'Settlement transaction confirmed on blockchain, funds distributed',
        colorScheme: 'green',
        icon: FiCheckCircle,
        sortOrder: 2
      },
      {
        id: 'srs_failed',
        displayText: 'Failed',
        description: 'Settlement transaction failed or rejected',
        colorScheme: 'red',
        icon: FiXCircle,
        sortOrder: 3
      }
    ]
  },

  // Refund Operations Status
  refund_operations_status: {
    prefix: 'ros_',
    defaultStatus: 'ros_failed',
    statuses: [
      {
        id: 'ros_refunded',
        displayText: 'Refunded',
        description: 'Refund successfully completed',
        colorScheme: 'green',
        icon: FiCheckCircle,
        sortOrder: 1
      },
      {
        id: 'ros_failed',
        displayText: 'Failed',
        description: 'Refund operation failed',
        colorScheme: 'red',
        icon: FiXCircle,
        sortOrder: 2
      }
    ]
  },

  // Contract Initialization Status (special case - no prefix)
  contract_initialization_status: {
    prefix: '',
    defaultStatus: 'not_initialized',
    statuses: [
      {
        id: 'initialized',
        displayText: 'Initialized',
        description: 'Contract successfully initialized with payment information',
        colorScheme: 'green',
        icon: FiCheckCircle,
        sortOrder: 1
      },
      {
        id: 'not_initialized',
        displayText: 'Not Initialized',
        description: 'Contract deployed but not yet initialized',
        colorScheme: 'yellow',
        icon: FiAlertTriangle,
        sortOrder: 2
      },
      {
        id: 'failed',
        displayText: 'Failed',
        description: 'Initialization attempted but failed',
        colorScheme: 'red',
        icon: FiXCircle,
        sortOrder: 3
      },
      {
        id: 'not_required',
        displayText: 'Not Required',
        description: 'Initialization not required for this contract',
        colorScheme: 'gray',
        icon: FiCircle,
        sortOrder: 4
      }
    ]
  },

  // Subscription Status (no prefix)
  subscription_status: {
    prefix: '',
    defaultStatus: 'active',
    statuses: [
      {
        id: 'active',
        displayText: 'Active',
        description: 'Subscription is active and valid',
        colorScheme: 'green',
        icon: FiCheckCircle,
        sortOrder: 1
      },
      {
        id: 'paused',
        displayText: 'Paused',
        description: 'Subscription temporarily paused',
        colorScheme: 'yellow',
        icon: FiPauseCircle,
        sortOrder: 2
      },
      {
        id: 'cancelled',
        displayText: 'Cancelled',
        description: 'Subscription has been cancelled',
        colorScheme: 'red',
        icon: FiXCircle,
        sortOrder: 3
      },
      {
        id: 'expired',
        displayText: 'Expired',
        description: 'Subscription has expired',
        colorScheme: 'gray',
        icon: FiClock,
        sortOrder: 4
      }
    ]
  },

  // Admin Role
  admin_role: {
    prefix: 'arl_',
    defaultStatus: 'arl_viewer',
    statuses: [
      {
        id: 'arl_superadmin',
        displayText: 'Super Admin',
        description: 'Full system access and control',
        colorScheme: 'purple',
        icon: FiShield,
        sortOrder: 1
      },
      {
        id: 'arl_admin',
        displayText: 'Admin',
        description: 'Administrative access with some restrictions',
        colorScheme: 'blue',
        icon: FiUserCheck,
        sortOrder: 2
      },
      {
        id: 'arl_operator',
        displayText: 'Operator',
        description: 'Operational access for daily tasks',
        colorScheme: 'green',
        icon: FiUser,
        sortOrder: 3
      },
      {
        id: 'arl_viewer',
        displayText: 'Viewer',
        description: 'Read-only access',
        colorScheme: 'gray',
        icon: FiEye,
        sortOrder: 4
      }
    ]
  },

  // Wallet Connection Status (no prefix)
  wallet_connection_status: {
    prefix: '',
    defaultStatus: 'pending',
    statuses: [
      {
        id: 'pending',
        displayText: 'Pending',
        description: 'Wallet connection pending',
        colorScheme: 'yellow',
        icon: FiClock,
        sortOrder: 1
      },
      {
        id: 'verified',
        displayText: 'Verified',
        description: 'Wallet successfully verified',
        colorScheme: 'green',
        icon: FiCheckCircle,
        sortOrder: 2
      },
      {
        id: 'failed',
        displayText: 'Failed',
        description: 'Wallet verification failed',
        colorScheme: 'red',
        icon: FiXCircle,
        sortOrder: 3
      }
    ]
  },

  // API Key Status (no prefix)
  api_key_status: {
    prefix: '',
    defaultStatus: 'active',
    statuses: [
      {
        id: 'active',
        displayText: 'Active',
        description: 'API key is active and usable',
        colorScheme: 'green',
        icon: FiUnlock,
        sortOrder: 1
      },
      {
        id: 'revoked',
        displayText: 'Revoked',
        description: 'API key has been revoked',
        colorScheme: 'red',
        icon: FiLock,
        sortOrder: 2
      }
    ]
  },

  // Merchant Wallet Status
  merchant_wallet_status: {
    prefix: '',
    defaultStatus: 'active',
    statuses: [
      {
        id: 'active',
        displayText: 'Active',
        description: 'Wallet address is active',
        colorScheme: 'green',
        icon: FiCheckCircle,
        sortOrder: 1
      },
      {
        id: 'revoked',
        displayText: 'Revoked',
        description: 'Wallet address has been revoked',
        colorScheme: 'red',
        icon: FiXCircle,
        sortOrder: 2
      }
    ]
  }
}

// ============================================================================
// StatusManager Class
// ============================================================================

/**
 * Unified Status Manager
 * 
 * Provides a consistent API for all status types in the system.
 * Handles prefix normalization, validation, and configuration retrieval.
 */
export class StatusManager {
  /**
   * Normalize a status ID by adding or removing prefix as needed
   */
  static normalizeStatusId(statusType: StatusType, statusId: string | null | undefined): string {
    const config = STATUS_CONFIGURATIONS[statusType]
    if (!config) {
      return typeof statusId === 'string' ? statusId : ''
    }

    const { prefix = '', defaultStatus } = config
    const normalizedInput = typeof statusId === 'string' ? statusId.trim() : ''

    if (!normalizedInput) {
      return defaultStatus || (prefix ? `${prefix}default` : '')
    }

    // If no prefix is expected, return as-is
    if (!prefix) return normalizedInput

    // If already has the correct prefix, return as-is
    if (normalizedInput.startsWith(prefix)) return normalizedInput

    // Check if it has a wrong prefix (any xxx_ pattern)
    const wrongPrefixMatch = normalizedInput.match(/^[a-z]+_/)
    if (wrongPrefixMatch) {
      // Remove wrong prefix and add correct one
      const withoutPrefix = normalizedInput.substring(wrongPrefixMatch[0].length)
      return prefix + withoutPrefix
    }
    
    // Add prefix if missing
    return prefix + normalizedInput
  }

  /**
   * Get the complete configuration for a status
   */
  static getStatusConfig(statusType: StatusType, statusId?: string | null): StatusConfig | null {
    const typeConfig = STATUS_CONFIGURATIONS[statusType]
    if (!typeConfig) return null

    const normalizedId = this.normalizeStatusId(statusType, statusId)
    return typeConfig.statuses.find(s => s.id === normalizedId) || null
  }

  /**
   * Get display text for a status
   */
  static getDisplayText(statusType: StatusType, statusId?: string | null): string {
    const config = this.getStatusConfig(statusType, statusId)
    if (config?.displayText) {
      return config.displayText
    }
    if (statusId) {
      return statusId
    }
    const typeConfig = STATUS_CONFIGURATIONS[statusType]
    return typeConfig?.defaultStatus || ''
  }

  /**
   * Get color scheme for a status (Chakra UI compatible)
   */
  static getColorScheme(statusType: StatusType, statusId?: string | null): string {
    const config = this.getStatusConfig(statusType, statusId)
    return config?.colorScheme || 'gray'
  }

  /**
   * Get icon for a status
   */
  static getIcon(statusType: StatusType, statusId?: string | null): IconType | undefined {
    const config = this.getStatusConfig(statusType, statusId)
    return config?.icon
  }

  /**
   * Get description for a status
   */
  static getDescription(statusType: StatusType, statusId?: string | null): string | undefined {
    const config = this.getStatusConfig(statusType, statusId)
    return config?.description
  }

  /**
   * Get sort order for a status
   */
  static getSortOrder(statusType: StatusType, statusId?: string | null): number {
    const config = this.getStatusConfig(statusType, statusId)
    return config?.sortOrder || 999
  }

  /**
   * Check if a status ID is valid for a given type
   */
  static isValidStatus(statusType: StatusType, statusId?: string | null): boolean {
    return this.getStatusConfig(statusType, statusId) !== null
  }

  /**
   * Get all statuses for a given type
   */
  static getAllStatuses(statusType: StatusType): StatusConfig[] {
    const typeConfig = STATUS_CONFIGURATIONS[statusType]
    if (!typeConfig) return []
    
    return [...typeConfig.statuses].sort((a, b) => 
      (a.sortOrder || 999) - (b.sortOrder || 999)
    )
  }

  /**
   * Get all status IDs for a given type
   */
  static getAllStatusIds(statusType: StatusType): string[] {
    return this.getAllStatuses(statusType).map(s => s.id)
  }

  /**
   * Get all display texts for a given type
   */
  static getAllDisplayTexts(statusType: StatusType): string[] {
    return this.getAllStatuses(statusType).map(s => s.displayText)
  }

  /**
   * Get status by display text (reverse lookup)
   */
  static getStatusByDisplayText(statusType: StatusType, displayText: string): StatusConfig | null {
    const typeConfig = STATUS_CONFIGURATIONS[statusType]
    if (!typeConfig) return null
    
    return typeConfig.statuses.find(s => s.displayText === displayText) || null
  }

  /**
   * Get the default status for a type
   */
  static getDefaultStatus(statusType: StatusType): string {
    const typeConfig = STATUS_CONFIGURATIONS[statusType]
    return typeConfig?.defaultStatus || ''
  }

  /**
   * Get dropdown options for a status type (useful for forms)
   */
  static getDropdownOptions(statusType: StatusType): Array<{ value: string; label: string }> {
    return this.getAllStatuses(statusType).map(status => ({
      value: status.id,
      label: status.displayText
    }))
  }
}

// ============================================================================
// Convenience Functions (for backward compatibility and ease of use)
// ============================================================================

/**
 * Quick access function to get display text
 */
export const getStatusText = (statusType: StatusType, statusId?: string | null): string => 
  StatusManager.getDisplayText(statusType, statusId)

/**
 * Quick access function to get color scheme
 */
export const getStatusColor = (statusType: StatusType, statusId?: string | null): string => 
  StatusManager.getColorScheme(statusType, statusId)

/**
 * Quick access function to get icon
 */
export const getStatusIcon = (statusType: StatusType, statusId?: string | null): IconType | undefined => 
  StatusManager.getIcon(statusType, statusId)

export const normalizeOrderStatusCode = (statusId?: string | null): string => {
  const normalized = StatusManager.normalizeStatusId('order_status', statusId)
  return normalized.startsWith('ost_') ? normalized.slice(4) : normalized
}

/**
 * Quick access function to validate status
 */
export const isValidStatus = (statusType: StatusType, statusId?: string | null): boolean => 
  StatusManager.isValidStatus(statusType, statusId)

/**
 * Quick access function to get all statuses
 */
export const getAllStatuses = (statusType: StatusType): StatusConfig[] => 
  StatusManager.getAllStatuses(statusType)

export class AddressVerificationStatusUtils {
  static getDisplayText(statusId?: string | null): string {
    return StatusManager.getDisplayText('address_verification_status', statusId)
  }

  static getColorScheme(statusId?: string | null): string {
    return StatusManager.getColorScheme('address_verification_status', statusId)
  }

  static getStatusId(displayText: string): string {
    const config = StatusManager.getStatusByDisplayText('address_verification_status', displayText)
    return config?.id || 'avs_pending'
  }

  static isValidStatusId(statusId?: string | null): boolean {
    return StatusManager.isValidStatus('address_verification_status', statusId)
  }

  static getAllStatusIds(): string[] {
    return StatusManager.getAllStatusIds('address_verification_status')
  }

  static getAllDisplayTexts(): string[] {
    return StatusManager.getAllDisplayTexts('address_verification_status')
  }
}

export const getVerificationStatusText = (statusId?: string | null): string => 
  AddressVerificationStatusUtils.getDisplayText(statusId)

export const getVerificationStatusColor = (statusId?: string | null): string => 
  AddressVerificationStatusUtils.getColorScheme(statusId)

export const getVerificationStatusId = (displayText: string): string => 
  AddressVerificationStatusUtils.getStatusId(displayText)

export const isValidVerificationStatus = (statusId?: string | null): boolean => 
  AddressVerificationStatusUtils.isValidStatusId(statusId)

// ============================================================================
// Type Exports
// ============================================================================

// Types are already exported above

// Export specific status ID types for type safety
export type MerchantStatusId =
  | 'mst_pending_config'
  | 'mst_partial_active'
  | 'mst_active'
  | 'mst_suspended'
  | 'mst_terminated'
export type OrderStatusId =
  | 'ost_awaiting_payment'
  | 'ost_underpaid'
  | 'ost_fullpaid'
  | 'ost_overpaid'
  | 'ost_expired'
  | 'ost_settled'
  | 'ost_full_refunded'
  | 'ost_compliance_hold'
  | 'ost_closed'
export type AddressVerificationStatusId = 'avs_pending' | 'avs_verified' | 'avs_failed'
export type OrderReceiversRegistryStatusId = 'orrs_active' | 'orrs_on_hold' | 'orrs_deployed' | 'orrs_cancelled'
export type ContractDeploymentsStatusId = 'cds_successful' | 'cds_failed' | 'cds_reverted'
export type SettlementRecordsStatusId = 'srs_pending' | 'srs_settled' | 'srs_failed'
export type RefundOperationsStatusId = 'ros_refunded' | 'ros_failed'
export type ContractInitializationStatusId = 'initialized' | 'not_initialized' | 'failed' | 'not_required'
export type SubscriptionStatusId = 'active' | 'paused' | 'cancelled' | 'expired'
export type AdminRoleId = 'arl_superadmin' | 'arl_admin' | 'arl_operator' | 'arl_viewer'

// Display text types for forms and reverse lookups
export type AddressVerificationStatusDisplay = 'Pending' | 'Verified' | 'Failed'

// Default export
export default StatusManager
